import type { VehicleGroup } from '@cobliteam/common-types';
import { useVehicleGroup } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Icon from '@cobliteam/ui/icons';
import { useEffect, useState } from 'react';

import MultiSelectVehicleGroupsOverlay from './MultiSelectVehicleGroupsOverlay';

export interface MultiSelectVehicleGroupsProps {
  size?: 'sm' | 'md';
  selectedVehicleGroups: VehicleGroup[];
  isOpen?: boolean;
  placement?: any;
  title?: string;
  emptyLabel?: string;
  isApplying?: boolean;
  isLoading?: boolean;
  hideEmptyGroups?: boolean;
  onChange: (groups: VehicleGroup[]) => void;
  onCancel?: () => void;
  onClose?: () => void;
  onClear?: () => void;
}

const MultiSelectVehicleGroups = ({ size = 'md', selectedVehicleGroups, isOpen, title, hideEmptyGroups, onChange, onClear, onCancel }: MultiSelectVehicleGroupsProps) => {
  const { t } = useTranslation('multiselects');
  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups = [], isFetching, isLoading } = getVehicleGroups();

  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedVehicleGroups || []);

  const filteredGroups = hideEmptyGroups ? vehicleGroups?.filter((group) => Boolean(group.vehicleIds?.length)) : vehicleGroups;

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const verifySomethingChanges = (groupsToBeApplied: VehicleGroup[]) => {
    const hasSameElements = selectedVehicleGroups?.length === groupsToBeApplied?.length && selectedVehicleGroups.every((value) => groupsToBeApplied.find((d) => d.id === value.id));
    return !hasSameElements;
  };

  const onChangeSelect = (group: VehicleGroup) => {
    const newItemDraft = Array.from(draft);
    const isItemSelected = newItemDraft.some((draftItem) => draftItem.id === group.id);
    if (isItemSelected) newItemDraft.splice(newItemDraft.map((v) => v.id).indexOf(group.id), 1);
    if (!isItemSelected) newItemDraft.push(group);
    setDraft(newItemDraft);
  };

  const clearFilters = () => {
    setDraft([]);
    onClear?.();
  };

  const cancel = () => {
    setForceClosed(true);
    setDraft(selectedVehicleGroups || []);
    onCancel?.();
  };

  const applyFilter = () => {
    onChange(draft);
    setForceClosed(true);
  };

  return (
    <Dropdown
      onOpenChange={() => setForceClosed(false)}
      forceClose={forceClosed}
      overlay={
        <MultiSelectVehicleGroupsOverlay
          isLoading={isFetching || isLoading}
          vehicleGroups={filteredGroups}
          selectedGroups={draft}
          selectionChanged={verifySomethingChanges(draft)}
          onChange={onChangeSelect}
          onClearFilter={clearFilters}
          onApplyFilter={applyFilter}
          onCancel={cancel}
        />
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='folders' className='size-4' />}>
        {title || t('groups.title')} {(selectedVehicleGroups?.length || 0) > 0 ? `(${selectedVehicleGroups?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

MultiSelectVehicleGroups.displayName = 'MultiSelectVehicleGroups';

export default MultiSelectVehicleGroups;
